// Controle da gravação (uma por vez): cria a reunião no servidor, captura o
// áudio, envia os trechos para transcrição, publica o estado para o pop-up
// flutuante e gera o resumo no fim.

import { api } from "./api.js";
import * as store from "./store.js";
import { toast, fmtDateTime } from "./dom.js";

const listeners = new Map();
export function on(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  return () => listeners.get(event).delete(fn);
}
function emit(event, payload) {
  for (const fn of listeners.get(event) || []) {
    try { fn(payload); } catch (err) { console.error(err); }
  }
}

let settings = { language: "pt" };
export function setSettings(s) {
  settings = s;
}

// { meetingId, pageId, startedAt, app, auto, pausedMs, pausedAt, recorder, uploader, pending, levels }
export let current = null;
const finishing = new Set();
let lastPage = null;
export const lastPageId = () => lastPage;
export const isFinishing = (id) => finishing.has(id);

// Reuniões já buscadas: id -> {meeting, segments}
const meetings = new Map();
export const cachedMeeting = (id) => meetings.get(id);

export async function fetchMeeting(id) {
  const data = await api("GET", `/meetings/${id}`);
  meetings.set(id, data);
  return data;
}

export function elapsed() {
  if (!current) return 0;
  return (current.pausedAt || Date.now()) - current.startedAt - current.pausedMs;
}

function publish() {
  const r = current;
  if (r) {
    window.ordo.setRecording({
      status: r.pausedAt ? "paused" : "recording",
      meetingId: r.meetingId, pageId: r.pageId, app: r.app,
      startedAt: r.startedAt, pausedMs: r.pausedMs, pausedAt: r.pausedAt,
    });
  } else {
    window.ordo.setRecording({ status: finishing.size ? "finishing" : "idle" });
  }
  emit("state", r);
}

// Começa a gravar. Pelo bloco da página: {meetingId, pageId, title}.
// Pela chamada detectada: {app, auto, newPage: true} (cria a página em "Reuniões").
export async function start({ meetingId, pageId, title, app = null, auto = false, newPage = false } = {}) {
  if (current) {
    toast("Já existe uma transcrição em andamento. Encerre-a antes de começar outra.");
    return null;
  }
  if (!store.canTranscribe()) {
    toast("A transcrição de chamadas é só para administradores do Ordo.");
    return null;
  }
  const startedAt = Date.now();
  const body = {
    id: meetingId || crypto.randomUUID(),
    startedAt,
    app,
    language: settings.language || null,
    title: title || `Reunião${app ? " no " + app : ""} ${fmtDateTime(startedAt)}`,
  };
  if (pageId) body.page_id = pageId;
  else if (newPage) body.new_page = true;
  // Reserva a vaga já: dois cliques seguidos não começam duas gravações.
  current = { meetingId: body.id, pageId: pageId || null, startedAt, app, auto, pausedMs: 0, pausedAt: null, pending: 0, starting: true };
  emit("state", current);
  let meeting;
  try {
    meeting = await store.withRetry(() => api("POST", "/meetings", body), [1500, 3000]);
  } catch (err) {
    current = null;
    publish();
    toast("Não foi possível começar a transcrição: " + err.message, 8000);
    return null;
  }
  const rec = current;
  rec.pageId = meeting.page_id || pageId || null;
  meetings.set(meeting.id, { meeting: { ...meeting, status: "recording", notes: "", summary: null }, segments: [] });
  rec.uploader = new window.OrdoRecorder.Uploader(
    async (job) => {
      const res = await api("PUT", `/meetings/${meeting.id}/chunks/${job.track}/${job.seq}?offsetMs=${job.offsetMs}`, job.wav, { raw: true, contentType: "audio/wav", timeout: 120000 });
      if (res.text) {
        const seg = { track: job.track, start_ms: job.offsetMs, text: res.text };
        meetings.get(meeting.id)?.segments.push(seg);
        emit("segment", { meetingId: meeting.id, seg });
      }
    },
    (pending) => { rec.pending = pending; emit("pending", pending); },
  );
  rec.recorder = new window.OrdoRecorder.Recorder({
    onChunk: (job) => rec.uploader.enqueue(job),
    onLevel: (track, level) => emit("level", { track, level }),
    onWarning: (msg) => toast(msg, 8000),
  });
  try {
    await rec.recorder.start();
  } catch (err) {
    current = null;
    publish();
    api("DELETE", `/meetings/${meeting.id}`).catch(() => {});
    toast("Não foi possível acessar o áudio: " + err.message, 8000);
    return null;
  }
  rec.starting = false;
  publish();
  if (meeting.page_id && !pageId) store.pollSoon(0);
  return { meetingId: meeting.id, pageId: rec.pageId };
}

export function pause() {
  const r = current;
  if (!r || r.pausedAt || r.starting) return;
  r.recorder.pause();
  r.pausedAt = Date.now();
  publish();
}

export function resume() {
  const r = current;
  if (!r || !r.pausedAt) return;
  r.recorder.resume();
  r.pausedMs += Date.now() - r.pausedAt;
  r.pausedAt = null;
  publish();
}

export async function stop() {
  const rec = current;
  if (!rec || rec.starting) return;
  current = null;
  lastPage = rec.pageId;
  finishing.add(rec.meetingId);
  publish();
  emit("status", rec.meetingId);
  try {
    await rec.recorder.stop();
    await rec.uploader.drain();
    if (rec.uploader.failed.length) toast(`${rec.uploader.failed.length} trecho(s) não puderam ser transcritos.`, 8000);
    await summarize(rec.meetingId, Date.now(), rec.pageId);
  } catch (err) {
    toast("Erro ao finalizar: " + err.message, 8000);
  } finally {
    finishing.delete(rec.meetingId);
    publish();
    emit("status", rec.meetingId);
  }
}

export async function summarize(meetingId, endedAt, pageId) {
  finishing.add(meetingId);
  emit("status", meetingId);
  try {
    const res = await api("POST", `/meetings/${meetingId}/finish`, { endedAt: endedAt || Date.now() }, { timeout: 300000 });
    await fetchMeeting(meetingId).catch(() => {});
    if (res.status === "done" && document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
      const n = new Notification("Resumo pronto", { body: meetings.get(meetingId)?.meeting.title || "Sua reunião foi resumida." });
      n.onclick = async () => {
        window.ordo.showWindow();
        if (pageId) (await import("./router.js")).openPage(pageId);
      };
    }
  } finally {
    finishing.delete(meetingId);
    emit("status", meetingId);
  }
}

export async function saveNotes(meetingId, notes) {
  const m = meetings.get(meetingId);
  if (m) m.meeting.notes = notes;
  await api("PATCH", `/meetings/${meetingId}`, { notes });
}

export async function remove(meetingId) {
  await api("DELETE", `/meetings/${meetingId}`);
  meetings.delete(meetingId);
}

export function onLevel(fn) {
  return on("level", fn);
}
